import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Users, ClipboardList, Bell, CalendarClock, Send, RefreshCw, Copy, CheckCircle, MessageSquare, ThumbsDown } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sub, color = "slate" }) {
  const colors = {
    red:   "bg-red-50 text-red-700 border-red-100",
    green: "bg-green-50 text-green-700 border-green-100",
    blue:  "bg-blue-50 text-blue-700 border-blue-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <Card className={`border ${colors[color]} shadow-none`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide opacity-60">{label}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
          </div>
          <div className="opacity-20">
            <Icon className="w-10 h-10" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatFecha(iso) {
  if (!iso) return "-";
  return iso.split("T")[0].split("-").reverse().join("/");
}

function waLink(telefono, mensaje) {
  const num = telefono.replace(/\D/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(mensaje)}`;
}

function frecuenciaTexto(dosis) {
  const tabla = { 0.5: "cada 2 días", 1: "1 vez al día", 2: "cada 12 horas", 3: "cada 8 horas", 4: "cada 6 horas" };
  return tabla[dosis] || `${dosis} past./día`;
}

function buildMensaje(clienteNombre, notifs) {
  if (notifs.length === 1) return notifs[0].mensaje;
  const nombre = clienteNombre.trim();
  let msg = `Hola ${nombre} 👋\n\n`;
  msg += `Tus pastillas están por acabarse 💊\n\n`;
  notifs.forEach(n => {
    const mes = Math.round((n.dosis_diaria || 1) * 30);
    msg += `• *${n.medicamento_nombre}* — ${frecuenciaTexto(n.dosis_diaria)} (${mes} past. para 1 mes)\n`;
  });
  msg += `\n¿Vas a comprar tus próximas cajas? Responde *SÍ* e indícanos a qué dirección quieres que te las enviemos para confirmar tu pedido con el *10% de descuento*, o *NO* si no las vas a comprar esta vez.\n\n`;
  msg += `⚠️ _Recuerda que después de 2 rechazos consecutivos dejarás de ser parte del programa Ahorra con Farmavida._\n\n`;
  msg += `🏥 _Farmavida - Ahorra con Nosotros_`;
  return msg;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [enviarDialogOpen, setEnviarDialogOpen] = useState(false);
  const [notifSeleccionada, setNotifSeleccionada] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const load = async () => {
    try {
      const [sRes, nRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/notificaciones/pendientes`),
      ]);
      setStats(sRes.data);
      setNotifs(nRes.data.slice(0, 8));
    } catch { toast.error("Error al cargar datos"); }
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/erp/sync`);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Sync completo: ${data.notificaciones_creadas ?? data.coincidencias_encontradas} notificaciones actualizadas`);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar ERP");
    } finally {
      setSyncing(false);
    }
  };

  const abrirEnviar = (grupo) => {
    // grupo = { cliente_nombre, telefono, notifs: [...] }
    setNotifSeleccionada(grupo);
    setCopiado(false);
    setEnviarDialogOpen(true);
  };

  const copiarMensaje = () => {
    const msg = buildMensaje(notifSeleccionada.cliente_nombre, notifSeleccionada.notifs);
    navigator.clipboard.writeText(msg);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const marcarEnviado = async () => {
    try {
      await Promise.all(
        notifSeleccionada.notifs.map(n =>
          axios.put(`${API}/notificaciones/${n.id}/marcar-enviado`)
        )
      );
      toast.success("Marcado como enviado");
      setEnviarDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al marcar");
    }
  };

  const marcarRechazado = async () => {
    const confirmado = window.confirm(
      `¿Registrar que ${notifSeleccionada.cliente_nombre} rechazó su compra?\n\nSi ya tiene 1 rechazo previo, su inscripción quedará inactiva por 6 meses.`
    );
    if (!confirmado) return;
    try {
      // Una sola llamada — el backend rechaza todas las notificaciones del cliente
      const { data: alguna } = await axios.put(`${API}/notificaciones/${notifSeleccionada.notifs[0].id}/rechazar`);
      if (alguna?.inactivada) {
        toast.warning(
          `Inscripción inactivada por 6 meses. Se reactivará el ${alguna.fecha_reactivacion}.`,
          { duration: 6000 }
        );
      } else {
        toast.info(`Rechazo #${alguna?.rechazos ?? 1} registrado. Al próximo rechazo la inscripción se inactivará por 6 meses.`);
      }
      setEnviarDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar rechazo");
    }
  };

  const hoy = new Date().toISOString().split("T")[0];

  // Agrupar notificaciones por cliente
  const notifGroups = Object.values(
    notifs.reduce((acc, n) => {
      const key = n.cliente_id;
      if (!acc[key]) acc[key] = {
        cliente_id: key,
        cliente_nombre: n.cliente_nombre,
        telefono: n.telefono,
        fecha_programada: n.fecha_programada,
        notifs: [],
      };
      // Usar la fecha más temprana como fecha del grupo
      if (n.fecha_programada < acc[key].fecha_programada) acc[key].fecha_programada = n.fecha_programada;
      acc[key].notifs.push(n);
      return acc;
    }, {})
  ).sort((a, b) => (a.fecha_programada > b.fecha_programada ? 1 : -1));

  const notifHoy = notifGroups.filter(g => g.fecha_programada <= hoy);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-sm text-slate-400">
            {new Date().toLocaleDateString("es-VE", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleSync}
          disabled={syncing}
          className="border-slate-200 text-slate-600 hover:text-slate-800"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Sincronizando ERP…" : "Sincronizar ERP"}
        </Button>
      </div>

      {/* Alerta del día */}
      {notifHoy.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-red-600 shrink-0" />
            <div>
              <p className="font-semibold text-red-800 text-sm">
                {notifHoy.length} recordatorio{notifHoy.length > 1 ? "s" : ""} para enviar hoy
              </p>
              <p className="text-xs text-red-500">Clientes cuyas pastillas están por acabarse</p>
            </div>
          </div>
          <Button size="sm" className="bg-red-700 hover:bg-red-800 text-white shrink-0" onClick={() => navigate("/admin/notificaciones")}>
            Ver notificaciones
          </Button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Clientes activos" value={stats.clientes_activos} sub={`de ${stats.total_clientes} total`} color="blue" />
          <StatCard icon={ClipboardList} label="Inscripciones activas" value={stats.inscripciones_activas} sub={`de ${stats.total_inscripciones} total`} color="slate" />
          <StatCard icon={Bell} label="Notificaciones hoy" value={stats.notificaciones_hoy} sub="Para enviar" color={stats.notificaciones_hoy > 0 ? "red" : "slate"} />
          <StatCard icon={CalendarClock} label="Próximos 7 días" value={stats.proximas_7_dias} sub="Notificaciones programadas" color="green" />
        </div>
      )}

      {/* Upcoming notifications */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold text-slate-700">
            Próximas notificaciones
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/notificaciones")} className="text-xs text-slate-400">
            Ver todas →
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {notifs.length === 0 ? (
            <div className="px-6 py-8 text-center text-slate-400 text-sm">
              No hay notificaciones pendientes
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {notifGroups.map(grupo => {
                const esHoy = grupo.fecha_programada <= hoy;
                const meds = grupo.notifs.map(n => n.medicamento_nombre).join(" + ");
                const dosisSummary = grupo.notifs.map(n =>
                  n.dosis_diaria ? `${n.dosis_diaria} past./día · ${Math.round(n.dosis_diaria * 30)} past./mes` : null
                ).filter(Boolean);
                const dosisUnica = [...new Set(dosisSummary)];
                return (
                  <div key={grupo.cliente_id} className={`flex items-center justify-between px-6 py-3 ${esHoy ? "bg-red-50/50" : ""}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{grupo.cliente_nombre}</p>
                        {esHoy && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium shrink-0">HOY</span>}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs text-slate-400 truncate">{meds} · {grupo.telefono}</p>
                        {grupo.notifs[0]?.zona && (
                          <span className="shrink-0 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                            📍 {grupo.notifs[0].zona}
                          </span>
                        )}
                      </div>
                      {grupo.notifs.some(n => n.ultima_compra_producto) && (
                        <p className="text-xs text-slate-400 italic mt-0.5">
                          {grupo.notifs.filter(n => n.ultima_compra_producto).map(n => n.ultima_compra_producto).join(" · ")}
                        </p>
                      )}
                      {dosisUnica.length > 0 && (
                        <p className="text-xs text-slate-500 font-medium mt-0.5">{dosisUnica.join(" · ")}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 ml-4 shrink-0">
                      <span className={`text-xs font-medium ${esHoy ? "text-red-600" : "text-slate-500"}`}>
                        {formatFecha(grupo.fecha_programada)}
                      </span>
                      {esHoy && (
                        <Button size="sm" variant="ghost" onClick={() => abrirEnviar(grupo)} className="text-green-600 hover:bg-green-50 h-7 px-2">
                          <Send className="w-3 h-3 mr-1" /> Enviar
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de envío manual */}
      <Dialog open={enviarDialogOpen} onOpenChange={setEnviarDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Enviar mensaje a {notifSeleccionada?.cliente_nombre}
            </DialogTitle>
          </DialogHeader>
          {notifSeleccionada && (() => {
            const mensaje = buildMensaje(notifSeleccionada.cliente_nombre, notifSeleccionada.notifs);
            return (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-600 space-y-1">
                <div className="flex items-center gap-2">
                  <span>📱 {notifSeleccionada.telefono}</span>
                  {notifSeleccionada.notifs[0]?.zona && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                      📍 {notifSeleccionada.notifs[0].zona}
                    </span>
                  )}
                </div>
                {notifSeleccionada.notifs.map(n => (
                  <div key={n.id} className="pt-0.5">
                    <div className="flex gap-3 text-xs">
                      <span className="font-medium text-slate-700">💊 {n.medicamento_nombre}</span>
                      {n.dosis_diaria && (
                        <span className="text-slate-400">{n.dosis_diaria} past./día · {Math.round(n.dosis_diaria * 30)} past./mes</span>
                      )}
                    </div>
                    {n.ultima_compra_producto && (
                      <p className="text-xs text-slate-400 ml-4 mt-0.5">
                        Última compra: <span className="text-slate-500 italic">{n.ultima_compra_producto}</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                <p className="text-xs text-green-700 font-medium mb-2">Mensaje para WhatsApp:</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{mensaje}</p>
              </div>

              {/* Acciones */}
              <div className="flex flex-col gap-2">
                <a
                  href={waLink(notifSeleccionada.telefono, mensaje)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20b858] text-white rounded-md px-4 py-2.5 text-sm font-medium transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Abrir WhatsApp con mensaje listo
                </a>

                <Button
                  variant="outline"
                  onClick={copiarMensaje}
                  className="w-full border-slate-200"
                >
                  {copiado
                    ? <><CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Copiado</>
                    : <><Copy className="w-4 h-4 mr-2" /> Copiar mensaje</>
                  }
                </Button>

                <Button
                  onClick={marcarEnviado}
                  className="w-full bg-slate-700 hover:bg-slate-800 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marcar como enviado
                </Button>

                <Button
                  onClick={marcarRechazado}
                  variant="outline"
                  className="w-full border-orange-200 text-orange-600 hover:bg-orange-50"
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  El cliente rechazó su compra
                </Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
